import { useInfiniteQuery } from '@tanstack/react-query';
import useSlingApi from '@sdk/api/useSlingApi';
import useConfig from '@sdk/config/useConfig';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { SearchArticlesPayload } from '@sdk/newsfeed/types/apiTypes';
import useToast from '@sdk/common/toast/useToast';

import { useArticleSearchContext } from '@web/newsfeed/ArticleSearch/ArticleSearchProvider/ArticleSearchProvider';
import { getArticleSearchQueryKey } from '@web/newsfeed/utils/getQueryKey';
import { useNewsfeedMappers } from '@web/newsfeed/hooks/useNewsfeedMappers';
import { NEWSFEED_ARTICLE_SEARCH_CACHE_TIME } from '@web/newsfeed/consts/cache';

const ARTICLE_SEARCH_PAGE_SIZE = 20;

export const useGetArticleSearchResults = () => {
  const { FEATURE_FLAGS } = useConfig();
  const { showErrorToast } = useToast();
  const { isReady, mapAPIArticleToUI } = useNewsfeedMappers();
  const { page, searchPageId, searchQuery } = useArticleSearchContext();
  const queryKey = getArticleSearchQueryKey(searchPageId, searchQuery);
  const api = useSlingApi();

  return useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const config: SearchArticlesPayload = {
        text: searchQuery,
        page: pageParam?.pageNumber || 0,
        pageSize: ARTICLE_SEARCH_PAGE_SIZE,
      };

      if (FEATURE_FLAGS.ARTICLE_SEARCH_BY_PAGE) {
        config.channelId = page.id;
      }

      const { data } = await api.newsfeed.searchArticles(config);
      return data.map((article) => mapAPIArticleToUI(article));
    },

    onError: (error) => {
      showErrorToast(getErrorForToast(error));
    },
    cacheTime: NEWSFEED_ARTICLE_SEARCH_CACHE_TIME,
    enabled: isReady,
    getNextPageParam: (lastPage, allPages) => {
      const hasOlderArticles = lastPage.length >= ARTICLE_SEARCH_PAGE_SIZE;
      if (!hasOlderArticles) {
        return;
      }

      return { pageNumber: allPages.length };
    },
  });
};
